import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Card } from '../../../components/ui/card';
import { BarChart3, IndianRupee, Headphones, UserCheck, CalendarX, Layers, Clock } from 'lucide-react';
import { isMgr } from './demoConstants';
import { PageHeader } from './DemoUI';
import DemoReportPage from './DemoReportPage';
import DemoAdmissionRevenuePage from './DemoAdmissionRevenuePage';
import DemoCounsellorLoadPage from './DemoCounsellorLoadPage';
import DemoConversionsPage from './DemoConversionsPage';
import DemoNotConductedPage from './DemoNotConductedPage';
import DemoCategoryTab from './DemoCategoryTab';
import DemoLateEntryPage from './DemoLateEntryPage';

const VIEWS = [
  { key: 'overview', label: 'Overview', icon: BarChart3, hint: 'Every demo in the period', Comp: DemoReportPage },
  { key: 'trainer', label: 'By Trainer', icon: IndianRupee, hint: 'Admission revenue per trainer', Comp: DemoAdmissionRevenuePage, mgrOnly: true },
  { key: 'counsellor', label: 'By Counsellor', icon: Headphones, hint: 'Demo load and compliance', Comp: DemoCounsellorLoadPage, mgrOnly: true },
  { key: 'admissions', label: 'Admissions', icon: UserCheck, hint: 'Conversions and enrolment links', Comp: DemoConversionsPage },
  { key: 'not-conducted', label: 'Not Conducted', icon: CalendarX, hint: 'Lost slots by reason and month', Comp: DemoNotConductedPage },
  { key: 'late-entry', label: 'Late Entry', icon: Clock, hint: 'Counsellors who log demos late', Comp: DemoLateEntryPage, mgrOnly: true },
  { key: 'technology', label: 'By Technology', icon: Layers, hint: 'Grouped by the Books category behind each course', Comp: DemoCategoryTab, mgrOnly: true },
];

/** One analytics page with four views — replaces the old Report / Conversions / Revenue / Load tabs. */
export default function DemoAnalyticsPage() {
  const { user } = useAuth();
  const mgr = isMgr(user?.role);
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  const views = useMemo(() => VIEWS.filter((v) => !v.mgrOnly || mgr), [mgr]);
  const active = views.find((v) => v.key === sp.get('view')) || views[0];
  const Comp = active.Comp;

  return (
    <div className="space-y-6" data-testid="demo-analytics-page">
      <PageHeader icon={BarChart3} eyebrow="Trainer Demo" title="Demo Analytics"
        subtitle="Every demo number in one place — switch the view instead of hunting through menus."
        testid="demo-analytics-header" />

      <Card className="p-1.5 border inline-flex flex-wrap gap-1 w-full sm:w-auto" data-testid="analytics-view-switcher">
        {views.map((v) => {
          const on = v.key === active.key;
          return (
            <button
              key={v.key}
              onClick={() => setSp(v.key === views[0].key ? {} : { view: v.key }, { replace: true })}
              className={`flex-1 sm:flex-none flex items-center gap-2 px-3.5 py-2 rounded-lg text-[12.5px] font-medium transition-colors ${on ? 'bg-violet-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
              data-testid={`analytics-tab-${v.key}`}
            >
              <v.icon className="w-3.5 h-3.5" />
              <span>{v.label}</span>
            </button>
          );
        })}
      </Card>

      <Comp embedded key={active.key} />
    </div>
  );
}
